import {
  Alert,
  DataStatus,
  Hyperlink,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetCashReceiptV4Query} from '@dms/api/finance';
import {EntityResourceIds} from '@dms/api/shared';
import {
  SaleVehicleResponseBody,
  useRefundVehicleReservationDepositMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {buildArray, getApiDateTimeString} from 'shared/web';

interface CheckoutOrderAdvanceDepositWarningProps {
  saleVehicle?: SaleVehicleResponseBody;
}

export function CheckoutOrderAdvanceDepositWarning(props: CheckoutOrderAdvanceDepositWarningProps) {
  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.saleVehicle?.vehicleId ?? '',
    },
    {skip: isNil(props.saleVehicle?.vehicleId)}
  );

  const [
    hasCashRegisterDocumentReadPermission,
    hasVehicleCancelReservationDepositPermissions,
    hasVehicleRefundReservationDepositPermissions,
  ] = usePermissions({
    permissionKeys: [
      'readCashRegisterDocument',
      'vehicleCancelReservationDeposit',
      'vehicleRefundReservationDeposit',
    ],
    scopes: {
      vehicleCancelReservationDeposit: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle?.branchId,
      },
      vehicleRefundReservationDeposit: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle?.branchId,
      },
    },
  });

  const [refundAdvanceDeposit] = useRefundVehicleReservationDepositMutation();
  const {
    data: cashRegisterDocument,
    isLoading: isCashRegisterDocumentLoading,
    isError: isCashRegisterDocumentError,
  } = useGetCashReceiptV4Query(
    {
      cashRegisterDocumentId:
        props.saleVehicle?.activeReservation?.paidAdvanceDeposit?.incomeCashReceiptId ?? '',
    },
    {
      skip:
        !hasCashRegisterDocumentReadPermission ||
        !props.saleVehicle?.activeReservation?.paidAdvanceDeposit?.incomeCashReceiptId,
    }
  );

  const handleRefundAdvanceDeposit = () =>
    openConfirmDialog({
      text: i18n.t('entity.vehicle.labels.refundDeposit', {
        documentNumber: cashRegisterDocument?.number,
      }),
      onConfirm: () =>
        refundAdvanceDeposit({
          vehicleId: props.saleVehicle?.vehicleId ?? '',
          'X-Branch': props.saleVehicle?.branchId ?? '',
          body: {
            issuedAt: getApiDateTimeString(new Date()),
          },
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('entity.vehicle.labels.depositRefunded'));
          })
          .catch(handleApiError),
    });

  return (
    <DataStatus isLoading={isCashRegisterDocumentLoading} isError={isCashRegisterDocumentError}>
      <Show
        when={
          props.saleVehicle?.activeReservation?.paidAdvanceDeposit &&
          cashRegisterDocument?.state === 'taken'
        }
      >
        <Alert
          data-testid="alert-advanceDepositHasBeenCollected"
          message={i18n.t('entity.vehicle.labels.advanceDepositHasBeenCollected')}
          hyperlinks={buildArray<Hyperlink>().when(
            hasVehicleCancelReservationDepositPermissions &&
              hasVehicleRefundReservationDepositPermissions,
            {
              title: i18n.t('entity.vehicle.labels.refund'),
              onClick: handleRefundAdvanceDeposit,
            }
          )}
        />
      </Show>
    </DataStatus>
  );
}
